/**
 * Snowcubes Reviews Widget
 * Renders the public review surface from SnowcubesReviews core.
 */
(function initSnowcubesReviewsWidget(global) {
  'use strict';

  var Core = global.SnowcubesReviews;
  if (!Core || !global.HTMLElement || !global.customElements) return;

  // Stateless builders live in reviews-widget-components.js. In the browser
  // that file loads first and registers them on SnowcubesReviewsComponents;
  // under Node/jest we require it. The class below consumes them by reference.
  var Components = global.SnowcubesReviewsComponents;
  if ((!Components || !Components.createElement) && typeof module !== 'undefined' && module.exports) {
    try {
      Components = require('./reviews-widget-components.js');
    } catch (_) {
      Components = Components || {};
    }
  }
  if (!Components || !Components.createElement) return;

  var createElement = Components.createElement;
  var createStars = Components.createStars;
  var formatDate = Components.formatDate;
  var safeReviewAnchorId = Components.safeReviewAnchorId;
  var getTargetReviewIdFromLocation = Components.getTargetReviewIdFromLocation;
  var getTargetAutoloadMax = Components.getTargetAutoloadMax;
  var reviewFormCacheKey = 'scv=20260629-public-photo-gate';

  function withReviewFormCacheKey(url) {
    if (!url) return url;
    var hashIndex = url.indexOf('#');
    var base = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    var hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
    if (/[?&]scv=/.test(base)) return url;
    return base + (base.indexOf('?') >= 0 ? '&' : '?') + reviewFormCacheKey + hash;
  }

  class SnowcubesReviewsElement extends HTMLElement {
    connectedCallback() {
      if (this._hasLoaded) return;
      this._hasLoaded = true;
      this._targetReviewId = getTargetReviewIdFromLocation(global.location);
      this._targetAnchorId = this._targetReviewId ? safeReviewAnchorId(this._targetReviewId) : '';
      this._targetAutoloadCount = 0;
      this._targetAutoloadMax = getTargetAutoloadMax(this);
      this._sort = Core.normalizeReviewSort(this.getAttribute('data-default-sort'));
      this._filter = Core.normalizeReviewFilter(this.getAttribute('data-default-filter'));
      this._initialLoadPromise = null;

      if (!Core.isReviewsUIEnabled() && !this.hasAttribute('data-force')) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.setAttribute('aria-busy', 'true');
      this._startLoading();
    }

    _startLoading() {
      this.renderLoading();

      // Deep-linked targets (#snowcubes-review-X) load immediately so we
      // can scroll to the anchor. Otherwise defer the API call until the
      // widget enters the viewport — saves ~934 LOC of work + an API
      // round-trip on PDPs the customer never scrolls to. Core Web
      // Vitals win without behavior change for engaged readers.
      if (this._targetReviewId || typeof IntersectionObserver === 'undefined') {
        this._loadInitialPage();
        return;
      }
      this._observer = new IntersectionObserver(
        (entries, obs) => {
          if (entries.some(function (e) { return e.isIntersecting; })) {
            obs.disconnect();
            this._observer = null;
            this._loadInitialPage();
          }
        },
        { rootMargin: '200px 0px' }
      );
      this._observer.observe(this);
      this._scheduleLazyLoadFallback();
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this._loadFallbackTimer && typeof global.clearTimeout === 'function') {
        global.clearTimeout(this._loadFallbackTimer);
        this._loadFallbackTimer = null;
      }
    }

    clearCache() {
      return Core.clearCache();
    }

    _loadInitialPage() {
      if (this._initialLoadPromise) return this._initialLoadPromise;
      if (this._loadFallbackTimer && typeof global.clearTimeout === 'function') {
        global.clearTimeout(this._loadFallbackTimer);
        this._loadFallbackTimer = null;
      }
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      this._initialLoadPromise = this.loadPage(1, { replace: true });
      return this._initialLoadPromise;
    }

    _scheduleLazyLoadFallback() {
      if (typeof global.setTimeout !== 'function') return;
      var delay = Number(this.getAttribute('data-lazy-fallback-ms'));
      if (!Number.isFinite(delay) || delay < 0) delay = 1200;
      this._loadFallbackTimer = global.setTimeout(() => {
        this._loadFallbackTimer = null;
        if (this._lastResponse || this.getAttribute('aria-busy') !== 'true') return;
        this._loadInitialPage();
      }, delay);
    }

    getRequestOptions(page) {
      var explicitMock = this.getAttribute('data-mock-json-id') || '';
      var mockTemplate = this.getAttribute('data-mock-json-id-template') || '';
      var mockJsonId = explicitMock;
      if (!mockJsonId && mockTemplate) {
        var search = (global.location && global.location.search) || '';
        var hasMockParam = /[?&]sc_reviews_mock=1\b/.test(search);
        if (hasMockParam) mockJsonId = mockTemplate;
      }
      return {
        productId: this.getAttribute('data-shopify-product-id') || '',
        handle: this.getAttribute('data-product-handle') || '',
        page: page || 1,
        perPage: Number(this.getAttribute('data-per-page')) || Core.DEFAULT_PER_PAGE,
        apiBase: this.getAttribute('data-api-base') || Core.DEFAULT_API_BASE,
        mockJsonId: mockJsonId,
        sort: this._sort || Core.DEFAULT_SORT,
        filter: this._filter || Core.DEFAULT_FILTER,
        targetReviewId: page === 1 ? this._targetReviewId : '',
      };
    }

    loadPage(page, options) {
      var replace = !options || options.replace !== false;
      return Core.fetchReviews(this.getRequestOptions(page))
        .then((response) => {
          this._lastResponse = response;
          if (replace) this.render(response);
          else this.appendReviews(response);
          if (typeof Core.upsertAggregateJsonLd === 'function') {
            Core.upsertAggregateJsonLd({
              summary: response.summary,
              product: {
                id: this.getAttribute('data-shopify-product-id'),
                handle: this.getAttribute('data-product-handle'),
                title: this.getAttribute('data-product-title'),
                url: this.getAttribute('data-product-url'),
              },
            });
          }
          return this.resolveTargetReview(response);
        })
        .then(() => {
          this.setAttribute('aria-busy', 'false');
        })
        .catch((error) => {
          Core.debug('Widget fetch failed', error);
          this.renderUnavailableState();
          this.setAttribute('aria-busy', 'false');
        });
    }

    renderLoading() {
      this.textContent = '';
      var region = createElement('section', 'sc-reviews');
      region.setAttribute('role', 'region');
      region.setAttribute('aria-label', 'Customer reviews');
      region.appendChild(this.createHeader({ reviewCount: 0, averageRating: 0 }));
      region.appendChild(createElement('p', 'sc-reviews__status', 'Loading reviews'));
      this.appendChild(region);
    }

    renderUnavailableState() {
      this.hidden = false;
      this.textContent = '';
      var region = createElement('section', 'sc-reviews');
      region.setAttribute('role', 'region');
      region.setAttribute('aria-label', 'Customer reviews');
      region.appendChild(this.createHeader({ reviewCount: 0, averageRating: 0 }));
      region.appendChild(this.createEmptyState({ unavailable: true }));
      this.appendChild(region);
    }

    render(response) {
      this.textContent = '';
      var region = createElement('section', 'sc-reviews');
      region.setAttribute('role', 'region');
      region.setAttribute('aria-label', 'Customer reviews');

      region.appendChild(this.createHeader(response.summary));

      if (!response.summary.reviewCount || response.reviews.length === 0) {
        region.appendChild(this.createEmptyState());
      } else {
        region.appendChild(this.createControls());
        var list = createElement('div', 'sc-reviews__list');
        list.setAttribute('data-reviews-list', '');
        response.reviews.forEach((review) => list.appendChild(this.createReview(review)));
        region.appendChild(list);
        if (response.pagination && response.pagination.hasMore) {
          region.appendChild(this.createShowMoreButton(response.pagination.page + 1));
        }
      }

      this.appendChild(region);
    }

    appendReviews(response) {
      var list = this.querySelector('[data-reviews-list]');
      if (!list) {
        this.render(response);
        return;
      }
      response.reviews.forEach((review) => list.appendChild(this.createReview(review)));
      var oldButton = this.querySelector('[data-reviews-show-more]');
      if (oldButton) oldButton.remove();
      if (response.pagination && response.pagination.hasMore) {
        this.querySelector('.sc-reviews').appendChild(this.createShowMoreButton(response.pagination.page + 1));
      }
    }

    resolveTargetReview(response) {
      if (!this._targetAnchorId || this._targetResolved) return Promise.resolve(false);
      var target = document.getElementById(this._targetAnchorId);
      if (target && this.contains(target)) {
        this._targetResolved = true;
        this._hideTargetNotFound();
        target.classList.add('sc-reviews__item--target');
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        if (typeof target.focus === 'function') {
          try {
            target.focus({ preventScroll: true });
          } catch (_) {
            target.focus();
          }
        }
        if (typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ block: 'center' });
        }
        return Promise.resolve(true);
      }

      // LR-11b: when the autoload window is exhausted OR no more pages
      // exist, render a non-blocking "Review not found" notice that links
      // back to the full review list. Hidden once a future render() finds
      // the target.
      var exhausted =
        !response || !response.pagination || !response.pagination.hasMore ||
        this._targetAutoloadCount >= this._targetAutoloadMax;
      if (exhausted) this._showTargetNotFound();
      if (
        response &&
        response.pagination &&
        response.pagination.hasMore &&
        this._targetAutoloadCount < this._targetAutoloadMax
      ) {
        this._targetAutoloadCount += 1;
        return this.loadPage(response.pagination.page + 1, { replace: false });
      }

      return Promise.resolve(false);
    }

    createHeader(summary) {
      var header = createElement('header', 'sc-reviews__header');
      var titleWrap = createElement('div', 'sc-reviews__title-wrap');
      titleWrap.appendChild(createElement('h2', 'sc-reviews__title', 'Reviews'));
      header.appendChild(titleWrap);

      var total = Number(summary.reviewCount) || 0;
      // Zero-state stays bare on rating data, but still gives every PDP a
      // direct review entry point.
      if (total > 0) {
        var aggregate = createElement('div', 'sc-reviews__aggregate');
        aggregate.appendChild(createElement('span', 'sc-reviews__rating', (Number(summary.averageRating) || 0).toFixed(1)));
        aggregate.appendChild(createStars(summary.averageRating || 0));
        aggregate.appendChild(createElement('span', 'sc-reviews__count', total === 1 ? '1 review' : total + ' reviews'));
        header.appendChild(aggregate);
      }

      var actions = createElement('div', 'sc-reviews__actions');
      var reviewLink = createElement('a', 'sc-reviews__review-link', 'Leave a review');
      reviewLink.href = this.getReviewFormUrl();
      actions.appendChild(reviewLink);
      if (total > 0) {
        var policyUrl = this.getAttribute('data-policy-url');
        if (policyUrl) {
          var policyLink = createElement('a', 'sc-reviews__policy-link', 'Review policy');
          policyLink.href = policyUrl;
          actions.appendChild(policyLink);
        }
      }
      header.appendChild(actions);
      return header;
    }

    createControls() {
      var controls = createElement('div', 'sc-reviews__controls');
      // Quiet-kitchen: a single, restrained Sort control. No marketplace-style
      // "Show" filter rail, no per-review verification badges, no helpful/not-
      // helpful voting — the reviews speak for themselves (brand-snowcubes:
      // detail-specificity over Amazon-coded trust-chrome).
      controls.appendChild(this.createChipControl({
        label: 'Sort',
        attr: 'data-reviews-sort',
        value: this._sort || Core.DEFAULT_SORT,
        options: [
          ['recent', 'Most recent'],
          ['highest', 'Highest rated'],
          ['lowest', 'Lowest rated'],
        ],
        onChange: (value) => {
          this._sort = Core.normalizeReviewSort(value);
          this.resetTargetAutoload();
          this.setAttribute('aria-busy', 'true');
          this.loadPage(1, { replace: true });
        },
      }));
      return controls;
    }

    createChipControl(config) {
      var wrap = createElement('div', 'sc-reviews__control');
      var label = createElement('span', 'sc-reviews__control-label', config.label);
      wrap.appendChild(label);

      var group = createElement('div', 'sc-reviews__chips');
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', config.label);
      group.setAttribute(config.attr, '');

      var current = config.value;
      var buttons = [];
      config.options.forEach(function (item) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sc-reviews__chip';
        btn.setAttribute('aria-pressed', String(item[0] === current));
        btn.setAttribute('data-chip-value', item[0]);
        btn.textContent = item[1];
        btn.addEventListener('click', function () {
          if (current === item[0]) return; // already active — no-op
          current = item[0];
          buttons.forEach(function (b) {
            b.setAttribute('aria-pressed', String(b.getAttribute('data-chip-value') === current));
          });
          config.onChange(current);
        });
        buttons.push(btn);
        group.appendChild(btn);
      });
      wrap.appendChild(group);
      return wrap;
    }

    resetTargetAutoload() {
      this._targetReviewId = '';
      this._targetAnchorId = '';
      this._targetResolved = true;
      this._targetAutoloadCount = 0;
    }

    getReviewFormUrl() {
      var explicit = this.getAttribute('data-review-url') || '';
      if (explicit) return withReviewFormCacheKey(explicit);
      var handle = this.getAttribute('data-product-handle') || '';
      var suffix = handle
        ? '?product=' + encodeURIComponent(handle) + '&' + reviewFormCacheKey
        : '?' + reviewFormCacheKey;
      return '/pages/review' + suffix + '#snowcubes-review-form';
    }

    createEmptyState(options) {
      var isUnavailable = !!(options && options.unavailable);
      var node = createElement('div', 'sc-reviews__empty');
      node.appendChild(createElement('p', 'sc-reviews__empty-title', isUnavailable ? 'Reviews are unavailable right now.' : 'No reviews yet.'));
      node.appendChild(
        createElement(
          'p',
          'sc-reviews__empty-copy',
          isUnavailable
            ? 'You can still leave a review while we get the list back.'
            : 'If you’ve tried Snowcubes, tell us what you noticed. We read every review before it appears.'
        )
      );
      return node;
    }

    createReview(review) {
      var article = createElement('article', 'sc-reviews__item');
      article.setAttribute('data-review-id', review.id || '');
      var anchorId = safeReviewAnchorId(review.id);
      if (anchorId) article.id = anchorId;

      var meta = createElement('div', 'sc-reviews__item-meta');
      meta.appendChild(createStars(review.rating));
      var date = formatDate(review.createdAt);
      if (date) meta.appendChild(createElement('time', 'sc-reviews__date', date));
      article.appendChild(meta);

      var heading = createElement('h3', 'sc-reviews__reviewer', review.reviewerName);
      article.appendChild(heading);

      if (review.title) article.appendChild(createElement('p', 'sc-reviews__item-title', review.title));
      if (review.body) article.appendChild(createElement('p', 'sc-reviews__body', review.body));
      if (review.photos && review.photos.length) article.appendChild(this.createPhotoGrid(review.photos));
      if (review.merchantReply && review.merchantReply.body) {
        article.appendChild(this.createMerchantReply(review.merchantReply));
      }
      return article;
    }

    createMerchantReply(reply) {
      var node = createElement('aside', 'sc-reviews__reply');
      // LR-12b: include reply date if available; surfaces "pending" badge
      // when reply has body but no createdAt (Wave 2 edge case for drafts).
      var label = createElement('p', 'sc-reviews__reply-label');
      label.appendChild(document.createTextNode('Snowcubes replied'));
      if (reply.createdAt) {
        var when = formatDate(reply.createdAt);
        if (when) {
          var dot = document.createElement('span');
          dot.className = 'sc-reviews__reply-dot';
          dot.setAttribute('aria-hidden', 'true');
          dot.textContent = ' · ';
          label.appendChild(dot);
          label.appendChild(createElement('time', 'sc-reviews__reply-date', when));
        }
      } else {
        var pending = createElement('span', 'sc-reviews__reply-pending', 'pending');
        label.appendChild(document.createTextNode(' '));
        label.appendChild(pending);
      }
      node.appendChild(label);
      node.appendChild(createElement('p', 'sc-reviews__reply-body', reply.body));
      return node;
    }

    _showTargetNotFound() {
      if (this._targetNotFoundShown) return;
      this._targetNotFoundShown = true;
      var existing = this.querySelector('[data-target-not-found]');
      if (existing) return;
      var notice = document.createElement('div');
      notice.className = 'sc-reviews__target-notfound';
      notice.setAttribute('data-target-not-found', '');
      notice.setAttribute('role', 'status');
      notice.textContent =
        "Couldn't find that exact review — showing all customer reviews instead.";
      var region = this.querySelector('.sc-reviews');
      if (region && region.firstChild) {
        region.insertBefore(notice, region.firstChild);
      } else {
        this.appendChild(notice);
      }
    }

    _hideTargetNotFound() {
      this._targetNotFoundShown = false;
      var existing = this.querySelector('[data-target-not-found]');
      if (existing) existing.remove();
    }

    createPhotoGrid(photos) {
      var grid = createElement('div', 'sc-reviews__photos');
      var self = this;
      photos.forEach(function (photo, index) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'sc-reviews__photo-button';
        button.setAttribute(
          'aria-label',
          'Open photo ' + (index + 1) + ' of ' + photos.length
        );

        var image = document.createElement('img');
        // Square, CDN-optimized thumbnail: Shopify resizes + center-crops on its
        // own CDN (no image pipeline of ours needed), so a tall/wide customer
        // photo becomes a clean square instead of a stretched strip — and we ship
        // ~400px instead of the full multi-MB original.
        var thumbSrc = photo.url;
        if (thumbSrc) {
          thumbSrc += (thumbSrc.indexOf('?') >= 0 ? '&' : '?') + 'width=400&height=400&crop=center';
        }
        image.src = thumbSrc;
        image.alt = photo.alt || 'Customer review photo';
        image.loading = 'lazy';
        image.decoding = 'async';
        // Square intrinsic size matches the cropped source + prevents layout shift.
        image.width = 400;
        image.height = 400;
        // Graceful fallback: a dead customer-photo URL drops its tile instead
        // of rendering a broken-image glyph.
        image.addEventListener('error', function () { button.remove(); });
        button.appendChild(image);

        button.addEventListener('click', function () {
          self.openLightbox(photos, index);
        });
        grid.appendChild(button);
      });
      return grid;
    }

    // LR-10: Lightbox modal for review photo gallery. Focus trap + ESC close
    // + arrow nav + click-outside-to-close. Restores prior focus on close.
    openLightbox(photos, startIndex) {
      this._lightboxIndex = startIndex || 0;
      this._lightboxPhotos = photos;
      this._lightboxPrevFocus = document.activeElement;

      var overlay = document.createElement('div');
      overlay.className = 'sc-reviews__lightbox';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Customer photo gallery');

      var close = document.createElement('button');
      close.type = 'button';
      close.className = 'sc-reviews__lightbox-close';
      close.setAttribute('aria-label', 'Close gallery');
      close.textContent = '×';

      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'sc-reviews__lightbox-prev';
      prev.setAttribute('aria-label', 'Previous photo');
      prev.textContent = '‹';

      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'sc-reviews__lightbox-next';
      next.setAttribute('aria-label', 'Next photo');
      next.textContent = '›';

      var stage = document.createElement('div');
      stage.className = 'sc-reviews__lightbox-stage';

      var image = document.createElement('img');
      image.className = 'sc-reviews__lightbox-img';
      image.alt = '';
      stage.appendChild(image);

      var caption = createElement('p', 'sc-reviews__lightbox-caption');
      var counter = createElement('p', 'sc-reviews__lightbox-counter');

      overlay.appendChild(close);
      overlay.appendChild(prev);
      overlay.appendChild(stage);
      overlay.appendChild(next);
      overlay.appendChild(caption);
      overlay.appendChild(counter);

      var self = this;
      function update() {
        var p = self._lightboxPhotos[self._lightboxIndex];
        image.src = p.url;
        image.alt = p.alt || '';
        caption.textContent = p.alt || '';
        counter.textContent =
          self._lightboxIndex + 1 + ' / ' + self._lightboxPhotos.length;
        prev.disabled = self._lightboxIndex === 0;
        next.disabled = self._lightboxIndex === self._lightboxPhotos.length - 1;
      }

      function gotoIndex(delta) {
        var n = self._lightboxIndex + delta;
        if (n < 0 || n >= self._lightboxPhotos.length) return;
        self._lightboxIndex = n;
        update();
      }

      close.addEventListener('click', function () { self.closeLightbox(); });
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) self.closeLightbox();
      });
      prev.addEventListener('click', function () { gotoIndex(-1); });
      next.addEventListener('click', function () { gotoIndex(1); });

      var keyHandler = function (e) {
        if (e.key === 'Escape') {
          e.preventDefault();
          self.closeLightbox();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          gotoIndex(-1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          gotoIndex(1);
        } else if (e.key === 'Tab') {
          // Focus trap — cycle within the 3 focusable buttons.
          var focusables = [close, prev, next].filter(function (b) { return !b.disabled; });
          if (focusables.length === 0) return;
          var i = focusables.indexOf(document.activeElement);
          if (e.shiftKey) {
            e.preventDefault();
            focusables[(i <= 0 ? focusables.length : i) - 1].focus();
          } else {
            e.preventDefault();
            focusables[(i + 1) % focusables.length].focus();
          }
        }
      };
      document.addEventListener('keydown', keyHandler);
      this._lightboxKeyHandler = keyHandler;

      document.body.appendChild(overlay);
      this._lightboxOverlay = overlay;
      update();
      close.focus();
    }

    closeLightbox() {
      if (this._lightboxOverlay) {
        this._lightboxOverlay.remove();
        this._lightboxOverlay = null;
      }
      if (this._lightboxKeyHandler) {
        document.removeEventListener('keydown', this._lightboxKeyHandler);
        this._lightboxKeyHandler = null;
      }
      if (this._lightboxPrevFocus && typeof this._lightboxPrevFocus.focus === 'function') {
        try { this._lightboxPrevFocus.focus(); } catch (_) {}
      }
    }

    createShowMoreButton(nextPage) {
      var button = createElement('button', 'sc-reviews__show-more', 'Show more');
      button.type = 'button';
      button.setAttribute('data-reviews-show-more', '');
      button.addEventListener('click', () => {
        button.disabled = true;
        this.loadPage(nextPage, { replace: false }).finally(() => {
          button.disabled = false;
        });
      });
      return button;
    }
  }

  if (!global.customElements.get('snowcubes-reviews')) {
    global.customElements.define('snowcubes-reviews', SnowcubesReviewsElement);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = SnowcubesReviewsElement;
  }
})(typeof window !== 'undefined' ? window : globalThis);
